import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useWorkspaces } from './useWorkspaces';
import { useToast } from './use-toast';
import { toError } from '@/lib/error-handling';
import type { 
  HermesEmailAccount, 
  CreateHermesEmailAccountRequest, 
  UpdateHermesEmailAccountRequest 
} from '@/types/hermes-email-account';

const STALE_TIME = 30 * 1000; // 30 seconds

// Get all email accounts for current workspace
export function useHermesEmailAccounts() {
  const { currentWorkspace } = useWorkspaces();

  return useQuery({
    queryKey: ['hermes-email-accounts', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) {
        throw new Error('No workspace selected');
      }

      console.log('Fetching hermes email accounts for workspace:', currentWorkspace.id);
      
      const { data, error } = await supabase
        .from('hermes_email_accounts')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Failed to fetch hermes email accounts:', error);
        throw error;
      }

      console.log('Loaded hermes email accounts:', data?.length || 0, 'accounts');
      return data as HermesEmailAccount[];
    },
    enabled: !!currentWorkspace?.id,
    staleTime: STALE_TIME,
  });
}

// Get single email account by ID
export function useHermesEmailAccount(accountId: string | undefined) {
  return useQuery({
    queryKey: ['hermes-email-account', accountId],
    queryFn: async () => {
      if (!accountId) {
        throw new Error('No account ID provided');
      }

      const { data, error } = await supabase
        .from('hermes_email_accounts')
        .select('*')
        .eq('id', accountId)
        .single();

      if (error) {
        console.error('Failed to fetch hermes email account:', error);
        throw error;
      }

      return data as HermesEmailAccount;
    },
    enabled: !!accountId,
    staleTime: STALE_TIME,
  });
}

// Create new email account
export function useCreateHermesEmailAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (accountData: CreateHermesEmailAccountRequest) => {
      console.log('Creating hermes email account:', accountData);
      
      const { data, error } = await supabase
        .from('hermes_email_accounts')
        .insert(accountData)
        .select()
        .single();

      if (error) {
        console.error('Failed to create hermes email account:', error);
        throw error;
      }

      console.log('Created hermes email account:', data.id);
      return data as HermesEmailAccount;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['hermes-email-accounts'] });
      queryClient.setQueryData(['hermes-email-account', data.id], data);
      
      toast({
        title: "Email Account Created",
        description: `Email account "${data.email_address}" created successfully`,
      });
    },
    onError: (error: any) => {
      console.error('Email account creation error:', error);
      
      let errorMessage = "Failed to create email account";
      if (error.message.includes('unique_account_per_workspace')) {
        errorMessage = "An account with this email address already exists in this workspace";
      } else if (error.message.includes('workspace_id')) {
        errorMessage = "Workspace not found";
      } else if (error.message.includes('user_id')) {
        errorMessage = "User authentication error";
      } else {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
}

// Update email account
export function useUpdateHermesEmailAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      accountId, 
      updates 
    }: { 
      accountId: string; 
      updates: UpdateHermesEmailAccountRequest 
    }) => {
      console.log('Updating hermes email account:', accountId, updates);
      
      const { data, error } = await supabase
        .from('hermes_email_accounts')
        .update(updates)
        .eq('id', accountId)
        .select()
        .single();

      if (error) {
        console.error('Failed to update hermes email account:', error);
        throw error;
      }

      console.log('Updated hermes email account:', data.id);
      return data as HermesEmailAccount;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['hermes-email-accounts'] });
      queryClient.setQueryData(['hermes-email-account', data.id], data);
      
      toast({
        title: "Email Account Updated",
        description: `Email account "${data.email_address}" updated successfully`,
      });
    },
    onError: (error: any) => {
      console.error('Email account update error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update email account",
        variant: "destructive",
      });
    },
  });
}

// Delete email account
export function useDeleteHermesEmailAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (accountId: string) => {
      console.log('Deleting hermes email account:', accountId);
      
      const { error } = await supabase
        .from('hermes_email_accounts')
        .delete()
        .eq('id', accountId);

      if (error) {
        console.error('Failed to delete hermes email account:', error);
        throw error;
      }

      console.log('Deleted hermes email account:', accountId);
      return accountId;
    },
    onSuccess: (accountId) => {
      queryClient.invalidateQueries({ queryKey: ['hermes-email-accounts'] });
      queryClient.removeQueries({ queryKey: ['hermes-email-account', accountId] });
      
      toast({
        title: "Email Account Deleted",
        description: "Email account deleted successfully",
      });
    },
    onError: (error: any) => {
      console.error('Email account deletion error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete email account",
        variant: "destructive",
      });
    },
  });
}

// Find or create a hermes account for an existing email_providers record
export function useFindOrCreateHermesAccount() {
  const { currentWorkspace } = useWorkspaces();

  return useMutation({
    mutationFn: async (emailProviderId: string) => {
      if (!currentWorkspace?.id) {
        throw new Error('No workspace selected');
      }

      const currentUser = await supabase.auth.getUser();
      if (!currentUser.data.user) {
        throw new Error('User not authenticated');
      }

      // Look up the email_providers record to get the email and type
      const { data: provider, error: providerError } = await supabase
        .from('email_providers')
        .select('id, email, type')
        .eq('id', emailProviderId)
        .single();

      if (providerError || !provider) {
        throw new Error('Email provider not found');
      }

      // Check if a hermes account already exists for this email in this workspace
      const { data: existing, error: findError } = await supabase
        .from('hermes_email_accounts')
        .select('id')
        .eq('email_address', provider.email)
        .eq('workspace_id', currentWorkspace.id)
        .single();

      if (existing) {
        return existing.id;
      }

      if (findError && findError.code !== 'PGRST116') {
        throw toError(findError);
      }

      // Create a new hermes account linked to this provider
      const { data: newAccount, error: createError } = await supabase
        .from('hermes_email_accounts')
        .insert({
          workspace_id: currentWorkspace.id,
          user_id: currentUser.data.user.id,
          email_address: provider.email,
          provider: provider.type, // 'gmail' | 'outlook'
        })
        .select('id')
        .single();

      if (createError) {
        throw toError(createError);
      }

      return newAccount.id;
    }
  });
}

// Find or create test account for workspace
export function useFindOrCreateTestAccount() {
  const { currentWorkspace } = useWorkspaces();

  return useMutation({
    mutationFn: async () => {
      if (!currentWorkspace?.id) {
        throw new Error('No workspace selected');
      }

      const currentUser = await supabase.auth.getUser();
      if (!currentUser.data.user) {
        throw new Error('User not authenticated');
      }

      // Try to find existing test account
      const { data: existingAccount, error: findError } = await supabase
        .from('hermes_email_accounts')
        .select('id')
        .eq('email_address', 'test@builditcrm.com')
        .eq('workspace_id', currentWorkspace.id)
        .single();

      if (existingAccount) {
        console.log('Found existing test account:', existingAccount.id);
        return existingAccount.id;
      }

      if (findError && findError.code !== 'PGRST116') {
        console.error('Error finding test account:', findError);
        throw findError;
      }

      // Create new test account directly
      console.log('Creating new test account for workspace:', currentWorkspace.id);
      const { data: newAccount, error: createError } = await supabase
        .from('hermes_email_accounts')
        .insert({
          workspace_id: currentWorkspace.id,
          user_id: currentUser.data.user.id,
          email_address: 'test@builditcrm.com',
          provider: 'webhook'
        })
        .select('id')
        .single();

      if (createError) {
        console.error('Error creating test account:', createError);
        throw createError;
      }

      console.log('Created new test account:', newAccount.id);
      return newAccount.id;
    }
  });
}